import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ConfigurationService, PathsService, UserManagementService } from '@draeger-academy/common';
import { MustMatch } from '@draeger-academy/common/lib/helpers/must-match.helper';
import { AuthenticationService } from '@draeger-academy/common/lib/services/authentication/authentication.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { FormsUtils } from '../../form-builder/shared/forms.utils';

@Component({
    selector: 'da-change-password-form',
    templateUrl: './change-password-form.component.html',
    styleUrls: ['./change-password-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ChangePasswordFormComponent implements OnInit {
    changePasswordForm: UntypedFormGroup;
    loading = false;
    submitted = false;
    message = false;
    showMessage = false;
    hasFocusCurrentPassword: boolean = false;
    hasFocusNewPassword: boolean = false;
    hasFocusRenewPassword: boolean = false;
    success: boolean = false;
    translationPrefix = 'changePassword';

    private _$user: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$loaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private _$showWrongOldPasswordMsg: BehaviorSubject<boolean> = new BehaviorSubject(false);

    constructor(
        private formBuilder: UntypedFormBuilder,
        private authenticationService: AuthenticationService,
        private userService: UserManagementService,
        private _configurationService: ConfigurationService,
        private _pathsService: PathsService
    ) {}

    public get user$(): Observable<boolean> {
        return this._$user.asObservable();
    }
    
    public get showWrongOldPasswordMsg$(): Observable<boolean> {
        return this._$showWrongOldPasswordMsg.asObservable();
    }

    public get loaded$(): Observable<{}> {
        return this._$loaded.asObservable();
    }

    // convenience getter for easy access to form fields
    linkPersonalData: string;
    get f() {
        return this.changePasswordForm.controls;
    }

    ngOnInit() {
        this.linkPersonalData = this._pathsService.getAccountPersonalDataLink();
        if (this._configurationService.isPartnerPage()) {
            this.translationPrefix = 'account.changePassword';
        }
        //Minimum eight characters, at least one uppercase letter, one lowercase letter, one number and one special character:
        const patternPassword = '^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-§/()\'~.]).{8,20}$';
        // Validators.pattern(patternPassword); Validators.minLength(8)

        this.changePasswordForm = this.formBuilder.group(
            {
                username: [''],
                currentPassword: ['', [Validators.required]],
                newPassword: ['', [Validators.required, Validators.pattern(patternPassword), Validators.minLength(8)]],
                renewPassword: ['', [Validators.required]]
            },
            {
                validator: MustMatch('newPassword', 'renewPassword')
            }
        );

        this.userService.getUser().then((user) => {
            this._$user.next(user);
            //this.f.username.value(user.email);
        });
    }

    public hasValue(fieldname) {
        const _input = fieldname;
        return _input.value ? FormsUtils.trim(_input.value.toString()) !== '' : false;
    }

    onSubmit() {
        this.submitted = true;
        this._$showWrongOldPasswordMsg.next(false);
        // stop here if form is invalid
        if (this.changePasswordForm.invalid) {
            return;
        }

        this._$loaded.next(false);
        this.userService.changePassword(this.f.username.value, this.f.currentPassword.value, this.f.newPassword.value).then(
            (data) => {
                if (data.token) {
                    this.success = true;
                    window.scrollTo(0, 0);
                    this.showMessage = true;
                    this._$loaded.next(true);
                } else {
                    this.authenticationService.logout().then(() => {
                        this.authenticationService.redirectAfterLogout();
                    });
                }
            },
            (error) => {
                console.log(error);
                if (error?.error === 'Wrong old password' && error?.status === 400) {                    
                    this._$showWrongOldPasswordMsg.next(true);
                } else {
                    this.success = false;
                    this.showMessage = true;
                    this._$loaded.next(true);
                }
            }
        );
    }
}
